import { analytics } from "@/content";
import { Rich } from "@/lib/rich";
import { useScrollReveal } from "@/hooks/useScrollReveal";

export default function AnalyticsAdvantages() {
  const { advantages } = analytics;
  const { ref: headerRef, revealed: headerRevealed } = useScrollReveal();

  return (
    <section id="advantages" className="py-24 bg-slate-50 dark:bg-[#0C0C0E] relative overflow-hidden">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-[5%] left-[-5%] w-[450px] h-[450px] rounded-full bg-blue-100/25 dark:bg-blue-500/[0.08] blur-[140px]" />
        <div className="absolute bottom-[10%] right-[-3%] w-[400px] h-[400px] rounded-full bg-emerald-100/20 dark:bg-emerald-500/[0.06] blur-[130px]" />
        <div className="absolute top-[40%] left-[30%] w-[300px] h-[300px] rounded-full bg-slate-200/30 dark:bg-white/[0.04] blur-[120px]" />
      </div>

      <div className="max-w-7xl mx-auto px-6 relative z-10">
        <div
          ref={headerRef}
          className={`text-center mb-16 transition-all duration-700 ${
            headerRevealed ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
          }`}
        >
          <span className="inline-block px-4 py-1.5 rounded-full bg-white dark:bg-white/[0.04] border border-slate-200 dark:border-white/[0.09] text-slate-500 dark:text-white/60 text-xs font-semibold tracking-widest uppercase mb-6 shadow-sm">
            {advantages.badge}
          </span>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-slate-900 dark:text-white leading-tight mb-4">
            <Rich text={advantages.title} accentClassName="text-blue-600 dark:text-blue-400" />
          </h2>
          {advantages.subtitle && (
            <p className="text-slate-600 dark:text-white/65 text-lg leading-relaxed max-w-2xl mx-auto">
              {advantages.subtitle}
            </p>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          {advantages.cards.map((card, index) => (
            <AdvantageCard
              key={card.title}
              card={card}
              index={index}
              keyLabel={advantages.keyLabel}
            />
          ))}
        </div>
      </div>
    </section>
  );
}

function AdvantageCard({
  card,
  index,
  keyLabel,
}: {
  card: { icon: string; title: string; text: string; key?: boolean };
  index: number;
  keyLabel: string;
}) {
  const { ref, revealed } = useScrollReveal();

  return (
    <div
      ref={ref}
      className={`group relative rounded-2xl p-8 transition-all duration-300 hover:-translate-y-1 cursor-default ${
        card.key
          ? "bg-white dark:bg-white/[0.04] border border-blue-100 dark:border-blue-400/25 shadow-md hover:shadow-lg hover:border-blue-300"
          : "bg-white dark:bg-white/[0.04] border border-slate-100 dark:border-white/[0.08] shadow-sm hover:shadow-md hover:border-slate-200"
      } ${revealed ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
      style={{ transitionDelay: `${index * 100}ms` }}
    >
      <div
        className={`w-12 h-12 flex items-center justify-center rounded-xl mb-5 transition-all duration-300 group-hover:scale-110 ${
          card.key ? "bg-blue-600" : "bg-blue-50 dark:bg-blue-500/[0.12]"
        }`}
      >
        <i
          className={`${card.icon} text-xl ${card.key ? "text-white" : "text-blue-600 dark:text-blue-400"}`}
        ></i>
      </div>

      {card.key && (
        <div className="absolute top-4 right-4 px-2.5 py-0.5 rounded-full bg-blue-600 text-white text-[10px] font-bold uppercase tracking-wide">
          {keyLabel}
        </div>
      )}

      <h3 className="text-lg font-bold mb-3 text-slate-900 dark:text-white">{card.title}</h3>
      <p className="text-slate-600 dark:text-white/65 text-sm leading-relaxed">{card.text}</p>
    </div>
  );
}
